import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Separator,
  Container,
  Typography,
} from '@ublend-npm/aulaui-core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';

import * as selectors from '@core/selectors/spaces';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { MY_SPACES_PAGE_VIEW } from '@core/constants/analytics';
import pushRoute from '@app/utils/routing';
import SpaceSelector from './SpaceSelector/SpaceSelector.container';
import SpacesSearch from './SpacesSearch.container';
import SpacesSearchInput from './SpacesSearchInput';
import SpacesShowArchivedSwitch from './SpacesShowArchivedSwitch';
import SpacesSortSelect from './SpacesSortSelect';
import RecentlyViewedSpaces from './RecentlyViewed/RecentlyViewed.conatiner';
import { withRouter } from 'react-router';
import { fetchRecentlyViewedSpaces } from '@core/spaceSelector/operations';
import { getCurrentSpaceId } from '@core/selectors/space';
import { AulaState } from '@core/types/state';

const ACTION_HEIGHT = 48;

interface Props {
  location?: {
    pathname?: string;
  };
}

const MySpaces = ({ location }: Props) => {
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<'az' | 'za'>('az');

  const dispatch = useDispatch();

  const pathRef = useRef('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const state = useSelector((state) => state);
  const spaceId = getCurrentSpaceId(state as AulaState);

  // Get spaces from props, or from redux if not passed as prop
  // const { spaces } = props;
  // If not passed as prop, get from redux:
  const spaces = useSelector((state) =>
    selectors.alphabeticallySorted(selectors.spacesData(state))
  );

  const { webAppFtSpaceArea202504 } = useFlags();
  if (!webAppFtSpaceArea202504) pushRoute('/dashboard');

  const analaticEvent = useCallback(async () => {
    await analyticsTrackEvent(MY_SPACES_PAGE_VIEW, {
      page_name: 'my_spaces',
    });
  }, []);

  // Changing document title and track page view analytics
  useEffect(() => {
    document.title = `My spaces - Aula`;
    analaticEvent();
  }, []);

  useEffect(() => {
    const pathname = location?.pathname || '';
    const wasOnMySpaces = pathRef.current.includes(
      `/dashboard/${spaceId}/spaces`
    );
    const isOnMySpaces = pathname.includes(`/dashboard/${spaceId}/spaces`);

    if (!wasOnMySpaces && isOnMySpaces) {
      dispatch(fetchRecentlyViewedSpaces());

      timeoutRef.current = setTimeout(() => {
        dispatch(fetchRecentlyViewedSpaces());
      }, 3200);
    }

    pathRef.current = pathname;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dispatch, location?.pathname]);

  const hasArchivedSpaces =
    Array.isArray(spaces) && spaces.some((space) => space && space.archived);

  return (
    <Container>
      <Typography
        title="My spaces"
        aria-label="My Space"
        variant="h4"
        tabIndex={0}
        sx={{
          mb: {
            xs: '16px',
            md: '24px',
          },
        }}
      >
        My spaces
      </Typography>

      {/* Search and filter section */}
      <Box
        mb={{
          xs: '16px',
          md: '24px',
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box flex={1} sx={{ height: `${ACTION_HEIGHT}px` }}>
            <SpacesSearchInput value={search} onChange={setSearch} />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={{ xs: 1, sm: 3 }}
            sx={{ height: `${ACTION_HEIGHT}px` }}
          >
            {hasArchivedSpaces && (
              <Box sx={{ ml: { xs: '16px', sm: 0 } }}>
                <SpacesShowArchivedSwitch
                  checked={showArchived}
                  onChange={setShowArchived}
                />
              </Box>
            )}
            <SpacesSortSelect value={sort} onChange={setSort} />
          </Box>
        </Box>
      </Box>
      <Grid>
        <SpacesSearch search={search} showArchived={showArchived} sort={sort} />
      </Grid>

      {/* Recently viewed section */}
      <Box mt={{ xs: 2, md: 3 }}>
        <RecentlyViewedSpaces />
      </Box>
      <Separator sx={{ my: '0.5rem' }} />

      {/* Space Selector Section */}
      <Box pb="5rem">
        <SpaceSelector
          // search={search}  //if need searching feature on spaceselector component need to pass this prop
          showArchived={showArchived}
          sort={sort}
        />
      </Box>
    </Container>
  );
};

export default withRouter(MySpaces);
